import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { P2GButton } from '@parcel2go/peacock-legacy-core/dist/button';
import { P2GDrawer } from '@parcel2go/peacock-legacy-core/dist/drawer';
import { P2GGrid } from '@parcel2go/peacock-legacy-core/dist/grid';
import { P2GText } from '@parcel2go/peacock-legacy-core/dist/typography';
import { P2GTextBox } from '@parcel2go/peacock-legacy-core/dist/textbox';
import { useP2GTranslation } from '@parcel2go/peacock-legacy-resources/dist/translations/hook';
import { P2GTranslation } from '@parcel2go/peacock-legacy-resources/dist/translations';
import { P2GBox } from '@parcel2go/peacock-legacy-core/dist/box';
import { P2GDivider } from '@parcel2go/peacock-legacy-core/dist/divider';
import { IP2GTheme } from '@parcel2go/peacock-legacy-themes/dist/types';
import { getLogger } from '@parcel2go/logging/dist/logging';

import { Case, CreateCaseNoteResponse } from '../../services/api/index';

const logger = getLogger();

const useCreateCaseStyles = makeStyles((theme: IP2GTheme) => ({
  drawerRoot: {
    minWidth: "375px",
    [theme.breakpoints.down('xs')]: {
      minWidth: "100%"
    }
  },
}));

const CreateCommunication: React.FC<ITrackingCase> = (props) => {

  const classes = useCreateCaseStyles();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();
  const [message, setMessage] = React.useState<string>("");

  async function send() {
    setLoading(true);
    const response = await props.onSendReply(props.caseDetails.id, message);
    if (response.success) {
      setOpen(false);
      setError(undefined);
    }
    else if (response.error) {
      logger.error("error sending case communication %o", response.error);
      setError(response.error.message);
    }
    else {
      logger.error("error sending case communication");
      setError("Failed");
    }
    setLoading(false);
  }

  return (
    <React.Fragment>
      <P2GButton colour="secondary" onClick={() => setOpen(true)}><P2GTranslation translation="TRACKING.RESULT.CASE.CREATE.OPEN" /></P2GButton>
      <P2GDrawer open={open} onClose={() => setOpen(false)} anchor="right">
        <P2GGrid container spacing={4} direction="column" classNames={classes.drawerRoot}>
          <P2GGrid item>
            <P2GText variant="h5"><P2GTranslation translation="TRACKING.RESULT.CASE.CREATE.TITLE" /></P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GTextBox id="txtCaseMessage" label={useP2GTranslation({ translation: "TRACKING.RESULT.CASE.CREATE.TEXTBOX" })} value={message} onChange={setMessage} multiline rows={15} rowsMax={20} />
          </P2GGrid>
          <P2GGrid item>
            <P2GButton processing={loading} onClick={send}><P2GTranslation translation="TRACKING.RESULT.CASE.CREATE.BUTTON" /></P2GButton>
          </P2GGrid>
          {error &&
            <P2GGrid item>
              <P2GText colour="error">{error}</P2GText>
            </P2GGrid>
          }
        </P2GGrid>
      </P2GDrawer>
    </React.Fragment>
  );
}

const CaseNotes: React.FC<ITrackingCase> = (props) => {
  return (
    <P2GGrid container direction="column" spacing={2}>
      {props.caseDetails.notes.map(n => {
        return <P2GGrid item>
          <P2GBox bordered p={2}>
            <P2GText component="p">{n.note}</P2GText>
            <P2GText component="p" variant="caption">{n.created}</P2GText>
          </P2GBox>
        </P2GGrid>
      })}
    </P2GGrid>
  );
}

interface ITrackingCase {
  caseDetails: Case,
  onSendReply: (caseId: string, note: string) => Promise<CreateCaseNoteResponse>
}

const TrackingCase: React.FC<ITrackingCase> = (props) => {

  return (
    <P2GGrid container direction="column" spacing={2}>
      <P2GGrid item>
        <P2GText colour="error"><P2GTranslation translation="TRACKING.RESULT.CASE.DETAIL.TITLE" options={{ id: props.caseDetails.id, created: new Date(props.caseDetails.created).toDateString() }} /></P2GText>
      </P2GGrid>
      {props.caseDetails.notes.length > 0 &&
        <P2GGrid item>
          <P2GText variant="h6" component="h6"><P2GTranslation translation="TRACKING.RESULT.CASE.DETAIL.NOTES" /></P2GText>
          <CaseNotes {...props} />
        </P2GGrid>
      }
      <P2GGrid item>
        <CreateCommunication {...props} />
      </P2GGrid>
      <P2GGrid item>
        <P2GDivider />
      </P2GGrid>
    </P2GGrid>
  );
}

export default TrackingCase;
